import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Copy, Plus, QrCode } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../../components/Navbar";
import Footer from "../../components/Footer";
import CommercialGate from "../../components/commercial/CommercialGate";
import { api } from "../../lib/api";
import { setPageTitle } from "../../lib/format";

const STATUS_CHIP = {
  active: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  draft: "bg-zinc-500/15 text-zinc-400 border-zinc-500/30",
  unpublished: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  suspended: "bg-red-500/15 text-red-400 border-red-500/30",
};

function PropertiesBody() {
  const [props_, setProps] = useState(null);
  const [drafts, setDrafts] = useState([]);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = () => {
    api.commercialProperties().then((r) => setProps(r.properties)).catch((e) => setError(e.message));
    api.commercialPropertyDrafts().then((r) => setDrafts(r.drafts || [])).catch(() => setDrafts([]));
  };
  useEffect(() => { setPageTitle("Commercial Properties"); load(); }, []);

  const deleteDraft = async (d) => {
    if (!window.confirm(`Delete draft "${d.name}"? This cannot be undone.`)) return;
    try {
      await api.commercialPropertyDraftDelete(d.draft_id);
      setDrafts((ds) => ds.filter((x) => x.draft_id !== d.draft_id));
      toast.success("Draft deleted");
    } catch (e) { toast.error(e.message); }
  };

  const publish = async (p, action) => {
    setBusyId(p.property_id);
    try {
      if (action === "publish") {
        await api.commercialPropertyPublish(p.property_id);
        toast.success(`${p.name} is now live — code ${p.property_code} accepts drivers.`);
      } else {
        await api.commercialPropertyUnpublish(p.property_id);
        toast.success(`${p.name} unpublished.`);
      }
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusyId("");
    }
  };

  const copyLink = (p) => {
    const base = window.location.origin;
    navigator.clipboard?.writeText(`${base}/p/${p.property_code}`);
    toast.success("Property link copied");
  };

  if (error) return <div className="card-dark p-8 text-center text-sm text-[#FF6B6B]" role="alert">{error}</div>;
  if (!props_) return <div className="grid sm:grid-cols-2 gap-4">{[...Array(2)].map((_, i) => <div key={i} className="card-dark h-44 animate-pulse" />)}</div>;
  const draftsBlock = drafts.length > 0 && (
    <div className="mb-8" data-testid="property-drafts">
      <p className="section-label">Drafts</p>
      <div className="mt-3 space-y-3">
        {drafts.map((d) => (
          <div key={d.draft_id} className="card-dark p-4 flex flex-wrap items-center gap-3" data-testid={`draft-${d.draft_id}`}>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <p className="font-semibold truncate">{d.name}</p>
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border bg-zinc-500/15 text-zinc-400 border-zinc-500/30">Draft</span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <div className="h-1.5 w-32 rounded-full bg-[#1F2328] overflow-hidden">
                  <div className="h-full rounded-full bg-[#E50914]" style={{ width: `${d.completion}%` }} />
                </div>
                <p className="text-[11px] text-zinc-500 tabular-nums">{d.completion}% complete · {d.photo_count} photo{d.photo_count === 1 ? "" : "s"}</p>
              </div>
            </div>
            <Link to={`/commercial/properties/new?draft=${d.draft_id}`} className="btn-secondary !py-2 !px-4 text-xs" data-testid={`draft-continue-${d.draft_id}`}>Continue</Link>
            <button onClick={() => deleteDraft(d)} className="text-xs text-zinc-500 hover:text-[#FF3B45] font-semibold" data-testid={`draft-delete-${d.draft_id}`}>Delete Draft</button>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {draftsBlock}
      {props_.length === 0 ? (
        <div className="card-dark p-12 text-center" data-testid="properties-empty">
          <p className="type-h3">No properties yet.</p>
          <p className="text-sm text-zinc-500 mt-2">Create your first commercial property.</p>
          <Link to="/commercial/properties/new" className="btn-primary inline-flex mt-6 text-sm" data-testid="properties-create-first">
            <Plus className="w-4 h-4" /> Create property
          </Link>
        </div>
      ) : (
    <div className="grid sm:grid-cols-2 gap-4" data-testid="properties-list">
      {props_.map((p) => (
        <div key={p.property_id} className="card-dark p-6 flex flex-col" data-testid={`property-card-${p.property_code}`}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2.5 flex-wrap">
                <h2 className="font-display font-bold text-lg truncate">{p.name}</h2>
                <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_CHIP[p.status] || STATUS_CHIP.draft}`} data-testid={`property-status-${p.property_code}`}>{p.status}</span>
              </div>
              <p className="text-xs text-zinc-500 mt-1.5 truncate">{p.address}</p>
            </div>
            <div className="text-right shrink-0">
              <p className="font-mono font-bold text-2xl tracking-[0.15em] text-[#FF3B45]">{p.property_code}</p>
              <p className="text-[9px] uppercase tracking-widest text-zinc-600">Property code</p>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-3 gap-3 text-center">
            <div className="rounded-xl bg-white/[0.03] py-2.5"><p className="font-bold">{p.capacity_total}</p><p className="text-[9px] uppercase tracking-wider text-zinc-500">Capacity</p></div>
            <div className="rounded-xl bg-white/[0.03] py-2.5"><p className="font-bold">{p.allocated ?? 0}</p><p className="text-[9px] uppercase tracking-wider text-zinc-500">Allocated</p></div>
            <div className="rounded-xl bg-white/[0.03] py-2.5"><p className="font-bold">{p.spots_active ?? 0}</p><p className="text-[9px] uppercase tracking-wider text-zinc-500">Live spots</p></div>
          </div>
          <div className="mt-5 flex flex-wrap gap-2">
            <Link to={`/commercial/properties/${p.property_id}`} className="btn-secondary !py-2 !px-4 text-xs" data-testid={`property-manage-${p.property_code}`}>
              Manage <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <button onClick={() => copyLink(p)} className="btn-ghost-red !py-2 !px-3 text-xs" data-testid={`property-copy-link-${p.property_code}`}>
              <Copy className="w-3.5 h-3.5" /> Link
            </button>
            <Link to={`/commercial/properties/${p.property_id}#qr`} className="btn-ghost-red !py-2 !px-3 text-xs" data-testid={`property-qr-${p.property_code}`}>
              <QrCode className="w-3.5 h-3.5" /> QR
            </Link>
            {p.status === "active" ? (
              <button onClick={() => publish(p, "unpublish")} disabled={busyId === p.property_id}
                      className="ml-auto text-xs text-amber-400 hover:text-amber-300 font-semibold disabled:opacity-50" data-testid={`property-unpublish-${p.property_code}`}>
                Unpublish
              </button>
            ) : p.status !== "suspended" ? (
              <button onClick={() => publish(p, "publish")} disabled={busyId === p.property_id}
                      className="ml-auto text-xs text-emerald-400 hover:text-emerald-300 font-semibold inline-flex items-center gap-1 disabled:opacity-50" data-testid={`property-publish-${p.property_code}`}>
                <Check className="w-3.5 h-3.5" /> Publish
              </button>
            ) : null}
          </div>
        </div>
      ))}
    </div>
      )}
    </>
  );
}

export default function CommercialProperties() {
  return (
    <CommercialGate>
      <div className="min-h-screen flex flex-col" data-testid="commercial-properties">
        <Navbar />
        <main className="flex-1 max-w-7xl w-full mx-auto px-5 sm:px-8 pt-28 pb-16">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="section-label">Commercial</p>
              <h1 className="type-display mt-3">Properties</h1>
            </div>
            <Link to="/commercial/properties/new" className="btn-primary text-sm" data-testid="properties-new">
              <Plus className="w-4 h-4" /> New property
            </Link>
          </div>
          <div className="mt-8"><PropertiesBody /></div>
        </main>
        <Footer />
      </div>
    </CommercialGate>
  );
}
